import { useTranslate } from 'react-admin';
import {
  Card,
  CardContent,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import { FiberManualRecord, Stop } from '@mui/icons-material';
import type { RunStatus, StepEvent, StreamError } from './types';

interface LiveStreamViewProps {
  status: RunStatus;
  steps: StepEvent[];
  error: StreamError | null;
  onAbort?: () => void;
}

// Renders the ordered list of streamed step lines (each step.summary) with a spinner header
// while running, plus an inline error banner on the `error` event. Partial steps already
// received remain visible after an error so the operator can edit and re-run.
export const LiveStreamView = ({ status, steps, error, onAbort }: LiveStreamViewProps) => {
  const translate = useTranslate();
  const isRunning = status === 'running';

  // Nothing to show before the first run.
  if (status === 'idle' && steps.length === 0 && !error) {
    return null;
  }

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isRunning && <CircularProgress size={18} />}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isRunning
                ? translate('aiTroubleshooting.stream.investigating')
                : translate('aiTroubleshooting.stream.title')}
            </Typography>
          </Box>
          {isRunning && onAbort && (
            <Button size="small" color="inherit" startIcon={<Stop />} onClick={onAbort}>
              {translate('aiTroubleshooting.stream.stop')}
            </Button>
          )}
        </Box>

        {steps.length > 0 && (
          <List dense disablePadding>
            {steps.map((step, idx) => (
              <ListItem key={`${step.stepIndex}-${idx}`} sx={{ py: 0.25, alignItems: 'flex-start' }}>
                <FiberManualRecord
                  sx={{ fontSize: 8, mt: '0.55rem', mr: 1, color: 'primary.main', flexShrink: 0 }}
                />
                <ListItemText
                  primary={step.summary}
                  slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
                />
              </ListItem>
            ))}
          </List>
        )}

        {isRunning && steps.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {translate('aiTroubleshooting.stream.waiting')}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: steps.length > 0 ? 2 : 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {error.code}
            </Typography>
            <Typography variant="body2">{error.message}</Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveStreamView;
